/**
 * Date formatting utility with user timezone, locale, and format preferences
 */

/**
 * Get user's timezone from auth store or default to browser timezone
 */
export function getUserTimezone(user) {
  return user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney';
}

/**
 * Get user's locale from auth store or default to browser locale
 */
export function getUserLocale(user) {
  return user?.locale || navigator.language || 'en-AU';
}

/**
 * Get user's date format preference
 */
export function getUserDateFormat(user) {
  return user?.dateFormat || 'DD/MM/YYYY';
}

/**
 * Get user's time format preference (12h or 24h)
 */
export function getUserTimeFormat(user) {
  return user?.timeFormat || '24h';
}

/**
 * Format a date using the user's preferred format
 * @param {Date|string} date - The date to format
 * @param {object} user - User object with preferences
 * @returns {string} Formatted date string
 */
export function formatDate(date, user = null) {
  if (!date) return '';
  
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const timezone = getUserTimezone(user);
  const locale = getUserLocale(user);
  const format = getUserDateFormat(user);
  
  return formatDateWithPattern(d, format, timezone, locale);
}

/**
 * Format a date with a specific pattern
 */
function formatDateWithPattern(date, pattern, timezone, locale) {
  // Get date parts in the user's timezone
  const options = { timeZone: timezone };
  
  const parts = {
    YYYY: date.toLocaleString(locale, { ...options, year: 'numeric' }),
    MMMM: date.toLocaleString(locale, { ...options, month: 'long' }),
    MMM: date.toLocaleString(locale, { ...options, month: 'short' }),
    MM: date.toLocaleString(locale, { ...options, month: '2-digit' }),
    DD: date.toLocaleString(locale, { ...options, day: '2-digit' }),
    D: date.toLocaleString(locale, { ...options, day: 'numeric' }),
  };
  
  // Use a single-pass approach with a map of all tokens
  // Process from longest to shortest to avoid partial matches
  const tokens = [
    { pattern: 'YYYY', value: parts.YYYY },
    { pattern: 'MMMM', value: parts.MMMM },
    { pattern: 'MMM', value: parts.MMM },
    { pattern: 'MM', value: parts.MM },
    { pattern: 'DD', value: parts.DD },
    { pattern: 'D', value: parts.D },
  ];
  
  // Build result by scanning through the pattern
  let result = '';
  let i = 0;
  
  while (i < pattern.length) {
    let matched = false;
    
    // Try to match each token (longest first due to array order)
    for (const token of tokens) {
      if (pattern.substring(i, i + token.pattern.length) === token.pattern) {
        // For single 'D', make sure it's not part of 'DD'
        if (token.pattern === 'D') {
          // Check if next char is also 'D' (would be DD)
          if (pattern[i + 1] === 'D') {
            continue; // Skip, let DD match instead
          }
        }
        result += token.value;
        i += token.pattern.length;
        matched = true;
        break;
      }
    }
    
    if (!matched) {
      result += pattern[i];
      i++;
    }
  }
  
  return result;
}

/**
 * Format a plain calendar date (a picked day, not an instant) with the
 * user's date-format pattern. No timezone conversion: a "YYYY-MM-DD" value
 * means that calendar day wherever it is read, so converting through a
 * timezone could shift it by a day.
 * @param {Date|string} date - Date at local midnight, or "YYYY-MM-DD"
 * @param {object} user - User object with preferences
 */
export const formatCalendarDate = (date, user = null) => {
  if (!date) return '';

  const d = date instanceof Date ? date : new Date(`${date}T00:00:00`);
  if (isNaN(d.getTime())) return '';

  return formatDateWithPattern(
    d,
    getUserDateFormat(user),
    undefined,
    getUserLocale(user),
  );
};

/**
 * The calendar date ("YYYY-MM-DD") that an instant falls on in the user's
 * timezone. Use instead of toISOString().split("T")[0], which reads the day
 * in UTC and can be off by one either side of it.
 */
export const toCalendarDateInUserTimezone = (date, user = null) => {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  // en-CA formats as YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: getUserTimezone(user),
  }).format(d);
};

/**
 * Today's calendar date in the user's timezone (local-midnight Date).
 * Around midnight the user's "today" can differ from the browser's.
 */
export const getTodayInUserTimezone = (user = null) => {
  const iso = toCalendarDateInUserTimezone(new Date(), user);
  return new Date(`${iso}T00:00:00`);
};

/**
 * The instant at which a picked calendar day ("YYYY-MM-DD") ends —
 * 23:59:59.999 — in the user's timezone, as a Date. Use when a date-only
 * pick (e.g. an expiration day) must become a stored timestamp: "expires on
 * 10 Sep" means end of 10 Sep where the organisation operates, regardless
 * of the browser's timezone.
 */
export const endOfDayInUserTimezone = (isoDate, user = null) => {
  const timeZone = getUserTimezone(user);
  const getOffsetMs = (instant) => {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
        .formatToParts(instant)
        .map((p) => [p.type, p.value]),
    );
    const asUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour) % 24,
      Number(parts.minute),
      Number(parts.second),
    );
    return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
  };

  // Start from the same wall-clock time in UTC, then shift by the zone's
  // offset; recompute once in case the shift crossed a DST transition.
  const utcGuess = new Date(`${isoDate}T23:59:59.999Z`);
  if (isNaN(utcGuess.getTime())) return null;
  let result = new Date(utcGuess.getTime() - getOffsetMs(utcGuess));
  const adjusted = new Date(utcGuess.getTime() - getOffsetMs(result));
  if (adjusted.getTime() !== result.getTime()) result = adjusted;
  return result;
};

/**
 * Format time using user's preference (12h or 24h)
 */
export function formatTime(date, user = null) {
  if (!date) return '';
  
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const timezone = getUserTimezone(user);
  const locale = getUserLocale(user);
  const timeFormat = getUserTimeFormat(user);
  
  return d.toLocaleString(locale, {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: timeFormat === '12h'
  });
}

/**
 * Format a date with time using user's preferences
 */
export function formatDateTime(date, user = null) {
  if (!date) return '';
  
  const dateStr = formatDate(date, user);
  const timeStr = formatTime(date, user);
  
  return `${dateStr} at ${timeStr}`;
}

/**
 * Format date for tables (using user's format)
 */
export function formatDateShort(date, user = null) {
  return formatDate(date, user);
}

/**
 * Format date for display (longer format with weekday)
 */
export function formatDateLong(date, user = null) {
  if (!date) return '';
  
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const timezone = getUserTimezone(user);
  const locale = getUserLocale(user);
  
  return d.toLocaleString(locale, {
    timeZone: timezone,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Get relative time string (e.g., "2 hours ago")
 */
export function formatRelativeTime(date) {
  if (!date) return '';
  
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const now = new Date();
  const diffMs = now - d;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 0) return 'just now'; // Future dates
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)}w ago`;
  
  return formatDateShort(d);
}

/**
 * Format relative time with full words
 */
export function formatRelativeTimeLong(date) {
  if (!date) return '';
  
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const now = new Date();
  const diffMs = now - d;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 0) return 'just now';
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  
  return formatDateShort(d);
}

export default {
  getUserTimezone,
  getUserLocale,
  getUserDateFormat,
  getUserTimeFormat,
  formatCalendarDate,
  getTodayInUserTimezone,
  toCalendarDateInUserTimezone,
  endOfDayInUserTimezone,
  formatDate,
  formatDateTime,
  formatTime,
  formatDateShort,
  formatDateLong,
  formatRelativeTime,
  formatRelativeTimeLong
};
